import { forwardRef, type HTMLAttributes, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SolarGlobalBoldDuotone } from 'components/icons/SolarGlobalBoldDuotone';
import {
    canSwitchAppLang,
    getAppLangCodes,
    setStoredLang,
} from './model/appLangs';

type ToggleProps = HTMLAttributes<HTMLSpanElement> & {
    children?: ReactNode;
};

/** Як ThemeButton: span.pc-head-link без btn-фону, що перекриває іконку. */
const SetLangToggle = forwardRef<HTMLSpanElement, ToggleProps>(
    ({ children, onClick, className, ...rest }, ref) => (
        <span
            ref={ref}
            role="button"
            tabIndex={0}
            className={`pc-head-link ms-0 cursor-pointer${className ? ` ${className}` : ''}`}
            onClick={(e: MouseEvent<HTMLSpanElement>) => {
                e.preventDefault();
                onClick?.(e);
            }}
            onKeyDown={(e: KeyboardEvent<HTMLSpanElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.(e as unknown as MouseEvent<HTMLSpanElement>);
                }
            }}
            {...rest}
        >
            {children}
        </span>
    ),
);
SetLangToggle.displayName = 'SetLangToggle';

const resolveCurrentCode = (codes: string[], language: string): string => {
    const base = String(language || '').split('-')[0];
    if (codes.includes(language)) return language;
    if (codes.includes(base)) return base;
    return codes[0];
};

const SetLang = () => {
    const { t, i18n } = useTranslation();
    const codes = getAppLangCodes();

    if (!canSwitchAppLang(codes)) return null;

    const currentCode = resolveCurrentCode(codes, i18n.language);
    const currentFlag = t(`SetLang.langs.${currentCode}.flag`, { defaultValue: '' });

    const handleSelect = (code: string) => {
        if (!code || code === i18n.language) return;
        i18n.changeLanguage(code);
        setStoredLang(code);
    };

    return (
        <div className="pc-h-item set-lang">
            <Dropdown align="end" className="set-lang-dropdown">
                <Dropdown.Toggle as={SetLangToggle} id="set-lang-dropdown">
                    {currentFlag
                        ? (
                            <span className="set-lang-flag" aria-hidden>
                                {currentFlag}
                            </span>
                        )
                        : <SolarGlobalBoldDuotone width="1.2em" height="1.2em" />}
                </Dropdown.Toggle>

                {/* Без pc-h-dropdown: його transform:none ламає Popper.
                    align=end — правий край меню = правий край кнопки */}
                <Dropdown.Menu
                    className="set-lang-menu"
                    align="end"
                    flip={false}
                    popperConfig={{
                        strategy: 'absolute',
                        modifiers: [
                            { name: 'preventOverflow', enabled: false },
                            { name: 'offset', options: { offset: [0, 4] } },
                        ],
                    }}
                >
                    {codes.map((code) => {
                        const active = code === currentCode;
                        const flag = t(`SetLang.langs.${code}.flag`, { defaultValue: '' });
                        const name = t(`SetLang.langs.${code}.name`, { defaultValue: code });
                        return (
                            <Dropdown.Item
                                key={code}
                                active={active}
                                onClick={() => handleSelect(code)}
                            >
                                {flag ? <span className="me-2" aria-hidden>{flag}</span> : null}
                                {name}
                            </Dropdown.Item>
                        );
                    })}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default SetLang;
export {
    canSwitchAppLang,
    getAppLangCodes,
    getAppLangs,
    getDefaultLangCode,
    getInitialLangCode,
} from './model/appLangs';
